import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchFeexPayDeliveries,
  fetchFeexPayWebhookStatus,
  getFeexPayWebhookPrettyUrl,
  getFeexPayWebhookUrl,
  sendFeexPayTestWebhook,
  type WebhookDelivery,
} from '@/lib/feexpayWebhook';
import EndpointCard from './components/EndpointCard';
import SecurityCard from './components/SecurityCard';
import EventsCard from './components/EventsCard';
import DeliveriesCard from './components/DeliveriesCard';

export default function FeexPayWebhookPage() {
  const url = useMemo(() => getFeexPayWebhookUrl(), []);
  const prettyUrl = useMemo(() => getFeexPayWebhookPrettyUrl(), []);

  const [status, setStatus] = useState<{ ok: boolean; secretConfigured: boolean; version: string | null; error: string | null }>({
    ok: false,
    secretConfigured: false,
    version: null,
    error: null,
  });
  const [statusLoading, setStatusLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const result = await fetchFeexPayWebhookStatus();
      setStatus(result);
    } catch (err) {
      setStatus({
        ok: false,
        secretConfigured: false,
        version: null,
        error: err instanceof Error ? err.message : 'Vérification impossible.',
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadDeliveries = useCallback(async () => {
    setDeliveriesLoading(true);
    setDeliveriesError(null);
    try {
      const rows = await fetchFeexPayDeliveries(25);
      setDeliveries(rows);
    } catch (err) {
      setDeliveriesError(err instanceof Error ? err.message : 'Lecture du journal impossible.');
    } finally {
      setDeliveriesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
    void loadDeliveries();
  }, [loadStatus, loadDeliveries]);

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await sendFeexPayTestWebhook();
      await loadDeliveries();
      return result;
    } finally {
      setTesting(false);
    }
  };

  const lastDelivery = deliveries[0];
  const last24h = deliveries.filter((d) => Date.now() - new Date(d.created_at).getTime() < 86400000).length;

  return (
    <div className="p-4 md:p-6 max-w-[1400px]">
      {/* En-tête */}
      <div className="mb-5">
        <Link
          to="/superadmin/settings"
          className="inline-flex items-center gap-1.5 text-[11px] text-foreground-500 hover:text-foreground-800 transition-colors mb-2"
        >
          <i className="ri-arrow-left-line"></i>
          Moyens de paiement
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Webhook FeexPay</h1>
            <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
              Recevez en temps réel chaque confirmation ou refus de paiement Mobile Money, avec la trace brute de
              ce que FeexPay a réellement répondu.
            </p>
          </div>
          <a
            href="https://docs.feexpay.me"
            target="_blank"
            rel="noopener noreferrer"
            className="h-9 px-4 rounded-full border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap flex items-center gap-1.5 hover:bg-background-100 transition-colors"
          >
            <i className="ri-external-link-line"></i>
            Documentation FeexPay
          </a>
        </div>
      </div>

      {/* Bandeau de synthèse */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className={`text-sm ${status.ok ? 'ri-checkbox-circle-fill text-green-600' : statusLoading ? 'ri-loader-4-line animate-spin text-foreground-400' : 'ri-plug-line text-amber-600'}`}></i>
            <span className="text-[10px] uppercase tracking-wide text-foreground-400">Endpoint</span>
          </div>
          <p className="text-sm font-bold text-foreground-950">
            {statusLoading ? 'Vérification…' : status.ok ? 'En ligne' : 'Injoignable'}
          </p>
          <p className="text-[10px] text-foreground-400 mt-0.5 truncate" title={url}>
            {url || 'URL indisponible'}
          </p>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className={`text-sm ${status.secretConfigured ? 'ri-lock-2-fill text-green-600' : 'ri-lock-unlock-line text-amber-600'}`}></i>
            <span className="text-[10px] uppercase tracking-wide text-foreground-400">Authentification</span>
          </div>
          <p className="text-sm font-bold text-foreground-950">
            {status.secretConfigured ? 'Bearer vérifié' : 'Aucun secret'}
          </p>
          <p className="text-[10px] text-foreground-400 mt-0.5">
            {status.secretConfigured ? 'Les appels non signés sont rejetés' : 'À configurer pour sécuriser l’endpoint'}
          </p>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-inbox-archive-line text-sm text-secondary-900"></i>
            <span className="text-[10px] uppercase tracking-wide text-foreground-400">Activité 24 h</span>
          </div>
          <p className="text-sm font-bold text-foreground-950">{last24h} livraison(s)</p>
          <p className="text-[10px] text-foreground-400 mt-0.5">
            {lastDelivery
              ? `Dernière : ${new Date(lastDelivery.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
              : 'Aucune réception pour le moment'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2">
          <EndpointCard
            url={url}
            prettyUrl={prettyUrl}
            onTest={handleTest}
            testing={testing}
            onRefresh={() => { void loadStatus(); void loadDeliveries(); }}
          />
        </div>
        <div className="lg:col-span-1">
          <SecurityCard secretConfigured={status.secretConfigured} statusError={status.error} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Formulaire côté FeexPay */}
        <div className="lg:col-span-2 rounded-lg border border-background-200/70 bg-background-50 p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-list-check-2 text-lg"></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-foreground-950">À renseigner dans FeexPay</h3>
              <p className="text-[11px] text-foreground-500 mt-0.5">
                FeexPay → Webhooks → « Ajouter un nouveau webhook »
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {[
              { field: 'Url', value: url || '—', note: 'L’URL complète de l’endpoint serveur.' },
              { field: 'Type d’en-tête', value: 'Bearer', note: 'Choisissez « Bearer » dans la liste déroulante.' },
              {
                field: 'Valeur de l’en-tête',
                value: status.secretConfigured ? 'le secret enregistré côté serveur' : 'à définir (voir encadré Authentification)',
                note: 'Identique au secret serveur, sinon les appels sont rejetés en 401.',
              },
              { field: 'Événements', value: 'Tous les événements', note: 'Le webhook ignore proprement ceux hors périmètre.' },
              { field: 'Sauvegarder', value: 'puis testez avec le bouton ci-dessus', note: 'Le test vérifie toute la chaîne sans modifier vos données.' },
            ].map((row) => (
              <div key={row.field} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 rounded-md border border-background-200/70 bg-background-50 px-3 py-2">
                <span className="text-[11px] font-semibold text-foreground-700 sm:w-40 flex-shrink-0">{row.field}</span>
                <span className="min-w-0">
                  <span className="block text-[11px] font-mono text-foreground-900 break-all">{row.value}</span>
                  <span className="block text-[10px] text-foreground-400 mt-0.5">{row.note}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5">
            <p className="text-[11px] text-foreground-700 leading-relaxed">
              <strong className="font-semibold">Ce qui se passe ensuite :</strong> chaque appel est authentifié, enregistré
              puis transmis au traitement métier — abonnement confirmé, commande boutique passée en « Confirmée »,
              parrain crédité. Un événement non reconnu n’est jamais perdu : il reste dans le journal.
            </p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <EventsCard />
        </div>
      </div>

      <DeliveriesCard
        deliveries={deliveries}
        loading={deliveriesLoading}
        error={deliveriesError}
        onRefresh={() => void loadDeliveries()}
      />
    </div>
  );
}